/**
 * TanStack Query hooks for distribution management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { distributionApi } from '../api';
import { barkKeys } from './useBark';
import type { QueueDistributionRequest } from '../types';

// ============================================================================
// Query Keys
// ============================================================================

export const distributionKeys = {
  all: ['distribution'] as const,
  site: (siteSlug?: string) => siteSlug ? [...distributionKeys.all, 'site', siteSlug] as const : distributionKeys.all,
  status: (contentId: string, siteSlug?: string) => [...distributionKeys.site(siteSlug), 'status', contentId] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/** Fetch distribution status for a content item */
export function useDistributionStatus(contentId: string, siteSlug?: string) {
  return useQuery({
    queryKey: distributionKeys.status(contentId, siteSlug),
    queryFn: () => distributionApi.getStatus(contentId, siteSlug),
    enabled: !!contentId,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/** Queue content for distribution */
export function useQueueDistribution(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: QueueDistributionRequest) => distributionApi.queue(data, siteSlug),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: distributionKeys.status(variables.content_id, siteSlug),
      });
    },
  });
}

/** Process the distribution queue */
export function useProcessQueue(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => distributionApi.process(siteSlug),
    onSuccess: () => {
      // Invalidate all distribution status queries
      queryClient.invalidateQueries({ queryKey: distributionKeys.site(siteSlug) });
      queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) });
    },
  });
}

/** Send a specific queue item immediately */
export function useSendNow(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (queueId: string) => distributionApi.sendNow(queueId, siteSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: distributionKeys.site(siteSlug) });
      queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) });
    },
  });
}

/** Cancel a queued item */
export function useCancelDistribution(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (queueId: string) => distributionApi.cancel(queueId, siteSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: distributionKeys.site(siteSlug) });
      queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) });
    },
  });
}

/** Retry a failed distribution item */
export function useRetryDistribution(siteSlug?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (queueId: string) => distributionApi.retry(queueId, siteSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: distributionKeys.site(siteSlug) });
      queryClient.invalidateQueries({ queryKey: barkKeys.site(siteSlug) });
    },
  });
}

/**
 * Fetch the manual copy-paste kit for a platform (AB#298): the transformed post
 * text + canonical URL, for platforms that must be posted by hand.
 */
export function useCopyPaste() {
  return useMutation({
    mutationFn: ({
      platform,
      data,
    }: {
      platform: string;
      data: { site_slug: string; content_type: string; content_id: string; locale?: string };
    }) => distributionApi.copyPaste(platform, data),
  });
}
